import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { selectablePreachers } from "../utils/preachers";
import { db, functions } from "../services/firebase";
import { Globals } from "../globals";
import { collection, getDocs } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import Preacher, { preacherConverter } from "../DTOs/Preacher";
import GemeindeNotificationSettings from "../DTOs/GemeindeNotificationSettings";
import { DEFAULT_CHANNEL_RELEASE } from "../DTOs/Gemeinde";
import { loadGemeindeSettings, saveGemeindeSettings } from "../services/gemeindeSettingsService";
import Form from "react-bootstrap/esm/Form";
import Button from "react-bootstrap/esm/Button";
import Card from "react-bootstrap/esm/Card";
import Spinner from "react-bootstrap/esm/Spinner";
import Alert from "react-bootstrap/esm/Alert";
import I_IMC_Props from "../infos/I_IMC_Props";
import { useAuth } from "../hooks/useAuth";
import DeliveryLog from "./DeliveryLog";
import { useGemeinde } from "../context/GemeindeContext";
import { gemeindeSubPath } from "../services/gemeindePaths";
import {
    isFcmSupported,
    getStoredFcmToken,
    subscribeToPushNotifications,
    unsubscribeFromPushNotifications,
} from "../services/fcmService";

/**
 * Gemeinde admin tab: which of the released channels this Gemeinde uses and who
 * stands in for preachers without contact details. Stored per Gemeinde in
 * gemeinden/{id}/config/notificationSettings – the document the Cloud Functions
 * read. The channel credentials themselves are central (superadmin only).
 */
export default function NotificationSettings({ setVariant, setMessage, setShowMessage }: I_IMC_Props) {
    const { t } = useTranslation();
    const { user } = useAuth();
    const { activeGemeindeId, activeGemeinde } = useGemeinde();
    // The superadmin decides per Gemeinde which channels may be used at all.
    const channels = activeGemeinde?.channels ?? DEFAULT_CHANNEL_RELEASE;
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [testingWhatsApp, setTestingWhatsApp] = useState<"predigt_erinnerung" | "hello_world" | null>(null);
    const [testPhoneNumber, setTestPhoneNumber] = useState("");
    const [preachers, setPreachers] = useState<Preacher[]>([]);
    const [config, setConfig] = useState<GemeindeNotificationSettings>(new GemeindeNotificationSettings());
    // Preacher self-service (same settings document).
    const [swapRequiresAdminApproval, setSwapRequiresAdminApproval] = useState(false);
    // GOD-35: reminders only with the preacher's recorded consent. Missing = off.
    const [consentRequired, setConsentRequired] = useState(false);
    const [allowDirectPreacherChange, setAllowDirectPreacherChange] = useState(true);

    // Push notification state
    const [pushToken, setPushToken] = useState<string | null>(null);
    const [pushPermission, setPushPermission] = useState<NotificationPermission>("default");
    const [pushBusy, setPushBusy] = useState(false);

    useEffect(() => {
        if (!isFcmSupported()) return;
        setPushPermission(Notification.permission);
        if (Notification.permission === "granted") {
            setPushToken(getStoredFcmToken());
        }
    }, []);

    const handleEnablePush = async () => {
        if (!user || !activeGemeindeId) return;
        setPushBusy(true);
        try {
            const token = await subscribeToPushNotifications(activeGemeindeId, user.uid);
            setPushToken(token);
            setPushPermission("granted");
            setVariant("success");
            setMessage(t("pushNotifications.activated"));
            setShowMessage(true);
        } catch (err: any) {
            if (err?.message === "permission-denied") {
                setPushPermission("denied");
            } else {
                setVariant("danger");
                setMessage(t("pushNotifications.error"));
                setShowMessage(true);
            }
        } finally {
            setPushBusy(false);
        }
    };

    const handleDisablePush = async () => {
        if (!pushToken || !activeGemeindeId) return;
        setPushBusy(true);
        try {
            await unsubscribeFromPushNotifications(activeGemeindeId, pushToken);
            setPushToken(null);
            setVariant("success");
            setMessage(t("pushNotifications.deactivated"));
            setShowMessage(true);
        } catch (_) {
            setVariant("danger");
            setMessage(t("errors.anErrorOccurred"));
            setShowMessage(true);
        } finally {
            setPushBusy(false);
        }
    };

    useEffect(() => {
        async function fetchData() {
            if (!activeGemeindeId) {
                setLoading(false);
                return;
            }
            try {
                // Load preachers
                const preacherSnap = await getDocs(
                    collection(db, ...gemeindeSubPath(activeGemeindeId, Globals.PREACHER_SUBCOLLECTION)).withConverter(preacherConverter)
                );
                const preacherList: Preacher[] = [];
                preacherSnap.forEach((d) => preacherList.push(d.data()));
                setPreachers(preacherList.sort((a, b) => a.surname.localeCompare(b.surname)));

                // Load this Gemeinde's notification settings
                const data = await loadGemeindeSettings(activeGemeindeId);
                setConfig(
                    new GemeindeNotificationSettings(
                        !!data.emailEnabled,
                        !!data.whatsappEnabled,
                        !!data.pushEnabled,
                        data.representativePreacherKey ?? "",
                        data.whatsappRepresentativePreacherKey ?? "",
                        !!data.emailOnlyToRepresentative,
                        !!data.whatsappOnlyToRepresentative
                    )
                );
                setSwapRequiresAdminApproval(!!data.swapRequiresAdminApproval);
                setConsentRequired(!!data.consentRequired);
                // Missing = allowed, so nothing changes for existing Gemeinden.
                setAllowDirectPreacherChange(data.allowDirectPreacherChange !== false);
            } catch (err) {
                setVariant("danger");
                setMessage(t("errors.loadingData"));
                setShowMessage(true);
            } finally {
                setLoading(false);
            }
        }
        fetchData();
    }, [setVariant, setMessage, setShowMessage, t, activeGemeindeId]);

    // In representative-only mode nothing is sent without a representative.
    const whatsappRepresentativeMissing =
        config.whatsappOnlyToRepresentative &&
        !config.whatsappRepresentativePreacherKey &&
        !config.representativePreacherKey;

    const handleSave = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!activeGemeindeId) return;
        setSaving(true);
        try {
            // Only the fields of this tab; choir and Synology settings in the same
            // document belong to the Gemeinde settings page and stay untouched.
            await saveGemeindeSettings(activeGemeindeId, {
                emailEnabled: config.emailEnabled,
                whatsappEnabled: config.whatsappEnabled,
                pushEnabled: config.pushEnabled,
                representativePreacherKey: config.representativePreacherKey,
                whatsappRepresentativePreacherKey: config.whatsappRepresentativePreacherKey,
                emailOnlyToRepresentative: config.emailOnlyToRepresentative,
                whatsappOnlyToRepresentative: config.whatsappOnlyToRepresentative,
                swapRequiresAdminApproval,
                allowDirectPreacherChange,
                consentRequired,
            });
            setVariant("success");
            setMessage(t("notificationSettings.saved"));
            setShowMessage(true);
        } catch (err) {
            setVariant("danger");
            setMessage(t("errors.anErrorOccurred"));
            setShowMessage(true);
        } finally {
            setSaving(false);
        }
    };

    const handleTestWhatsApp = async (templateType: "predigt_erinnerung" | "hello_world") => {
        setTestingWhatsApp(templateType);
        try {
            const sendTest = httpsCallable<
                { testPhoneNumber: string; templateType: string; gemeindeId: string | null },
                { success: boolean; recipient: string }
            >(functions, "sendTestWhatsAppNotification");
            // The Gemeinde decides which numbers an admin may test with (GOD-51).
            const result = await sendTest({ testPhoneNumber, templateType, gemeindeId: activeGemeindeId });
            setVariant("success");
            setMessage(t("notificationSettings.whatsappTestSuccess", { recipient: result.data.recipient }));
            setShowMessage(true);
        } catch (err: any) {
            setVariant("danger");
            setMessage(err?.message || t("errors.anErrorOccurred"));
            setShowMessage(true);
        } finally {
            setTestingWhatsApp(null);
        }
    };

    if (loading) {
        return (
            <div className="text-center p-4">
                <Spinner animation="border" />
            </div>
        );
    }

    return (
        <div>
            <h5 className="mb-3">{t("notificationSettings.heading")}</h5>
            <Alert variant="info">
                <p className="mb-0">{t("notificationSettings.description")}</p>
            </Alert>

            <Form onSubmit={handleSave}>
                {/* Representative preacher */}
                <Card className="mb-3">
                    <Card.Header>{t("notificationSettings.representativeSection")}</Card.Header>
                    <Card.Body>
                        <Form.Group>
                            <Form.Label>{t("notificationSettings.representativePreacher")}</Form.Label>
                            <Form.Select
                                value={config.representativePreacherKey}
                                onChange={(e) =>
                                    setConfig({ ...config, representativePreacherKey: e.target.value })
                                }
                            >
                                <option value="">{t("notificationSettings.noRepresentative")}</option>
                                {selectablePreachers(preachers, config.representativePreacherKey).map((p) => (
                                    <option key={p.key} value={p.key}>
                                        {p.name} {p.surname}
                                        {p.churchFunction ? ` (${p.churchFunction})` : ""}
                                        {p.email ? ` – ${p.email}` : ""}
                                    </option>
                                ))}
                            </Form.Select>
                            <Form.Text className="text-muted">
                                {t("notificationSettings.representativeHelp")}
                            </Form.Text>
                        </Form.Group>
                    </Card.Body>
                </Card>

                {/* Email */}
                <Card className="mb-3">
                    <Card.Header>
                        <div className="d-flex justify-content-between align-items-center">
                            <span>{t("notificationSettings.emailSection")}</span>
                            <Form.Check
                                type="switch"
                                id="emailEnabled"
                                label={t("notificationSettings.emailEnabled")}
                                checked={config.emailEnabled && channels.emailReleased}
                                disabled={!channels.emailReleased}
                                onChange={(e) => setConfig({ ...config, emailEnabled: e.target.checked })}
                            />
                        </div>
                    </Card.Header>
                    {!channels.emailReleased && (
                        <Card.Body className="text-muted">{t("notificationSettings.channelNotReleased")}</Card.Body>
                    )}
                    {/* Shown even while the channel is off, so it can be set before switching email on. */}
                    {channels.emailReleased && (
                        <Card.Body>
                            <Form.Check
                                type="switch"
                                id="emailOnlyToRepresentative"
                                label={t("notificationSettings.emailOnlyToRepresentative")}
                                checked={config.emailOnlyToRepresentative}
                                onChange={(e) => setConfig({ ...config, emailOnlyToRepresentative: e.target.checked })}
                            />
                            <Form.Text className={config.emailOnlyToRepresentative && !config.representativePreacherKey ? "text-danger" : "text-muted"}>
                                {config.emailOnlyToRepresentative && !config.representativePreacherKey
                                    ? t("notificationSettings.emailOnlyToRepresentativeMissing")
                                    : t("notificationSettings.emailOnlyToRepresentativeHelp")}
                            </Form.Text>
                        </Card.Body>
                    )}
                </Card>

                {/* WhatsApp */}
                <Card className="mb-3">
                    <Card.Header>
                        <div className="d-flex justify-content-between align-items-center">
                            <span>{t("notificationSettings.whatsappSection")}</span>
                            <Form.Check
                                type="switch"
                                id="whatsappEnabled"
                                label={t("notificationSettings.whatsappEnabled")}
                                checked={config.whatsappEnabled && channels.whatsappReleased}
                                disabled={!channels.whatsappReleased}
                                onChange={(e) => setConfig({ ...config, whatsappEnabled: e.target.checked })}
                            />
                        </div>
                    </Card.Header>
                    {!channels.whatsappReleased && (
                        <Card.Body className="text-muted">{t("notificationSettings.channelNotReleased")}</Card.Body>
                    )}
                    {/* Shown even while the channel is off, so it can be set before switching WhatsApp on. */}
                    {channels.whatsappReleased && (
                        <Card.Body>
                            <Form.Group className="mb-3">
                                <Form.Label>{t("notificationSettings.whatsappRepresentativePreacher")}</Form.Label>
                                <Form.Select
                                    value={config.whatsappRepresentativePreacherKey}
                                    onChange={(e) =>
                                        setConfig({ ...config, whatsappRepresentativePreacherKey: e.target.value })
                                    }
                                >
                                    <option value="">{t("notificationSettings.whatsappNoRepresentative")}</option>
                                    {selectablePreachers(preachers, config.whatsappRepresentativePreacherKey).map((p) => (
                                        <option key={p.key} value={p.key}>
                                            {p.name} {p.surname}
                                            {p.churchFunction ? ` (${p.churchFunction})` : ""}
                                            {p.phoneNumber ? ` – ${p.phoneNumber}` : ""}
                                        </option>
                                    ))}
                                </Form.Select>
                                <Form.Text className="text-muted">
                                    {t("notificationSettings.whatsappRepresentativeHelp")}
                                </Form.Text>
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Check
                                    type="switch"
                                    id="whatsappOnlyToRepresentative"
                                    label={t("notificationSettings.whatsappOnlyToRepresentative")}
                                    checked={config.whatsappOnlyToRepresentative}
                                    onChange={(e) => setConfig({ ...config, whatsappOnlyToRepresentative: e.target.checked })}
                                />
                                <Form.Text className={whatsappRepresentativeMissing ? "text-danger" : "text-muted"}>
                                    {whatsappRepresentativeMissing
                                        ? t("notificationSettings.whatsappOnlyToRepresentativeMissing")
                                        : t("notificationSettings.whatsappOnlyToRepresentativeHelp")}
                                </Form.Text>
                            </Form.Group>

                            {config.whatsappEnabled && (
                                <div className="d-flex gap-2 align-items-center flex-wrap">
                                    <Form.Control
                                        type="tel"
                                        size="sm"
                                        style={{ maxWidth: "200px" }}
                                        value={testPhoneNumber}
                                        onChange={(e) => setTestPhoneNumber(e.target.value)}
                                        placeholder="+491701234567"
                                    />
                                    <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        disabled={!!testingWhatsApp || !testPhoneNumber.trim()}
                                        onClick={() => handleTestWhatsApp("hello_world")}
                                    >
                                        {testingWhatsApp === "hello_world" ? (
                                            <>
                                                <Spinner as="span" animation="border" size="sm" className="me-2" />
                                                {t("notificationSettings.whatsappTesting")}
                                            </>
                                        ) : (
                                            "Test: hello_world"
                                        )}
                                    </Button>
                                    <Button
                                        variant="outline-success"
                                        size="sm"
                                        disabled={!!testingWhatsApp || !testPhoneNumber.trim()}
                                        onClick={() => handleTestWhatsApp("predigt_erinnerung")}
                                    >
                                        {testingWhatsApp === "predigt_erinnerung" ? (
                                            <>
                                                <Spinner as="span" animation="border" size="sm" className="me-2" />
                                                {t("notificationSettings.whatsappTesting")}
                                            </>
                                        ) : (
                                            t("notificationSettings.whatsappTest")
                                        )}
                                    </Button>
                                </div>
                            )}
                        </Card.Body>
                    )}
                </Card>

                {/* Consent (GOD-35) */}
                <Card className="mb-3" data-testid="consent-settings">
                    <Card.Header>{t("consent.settingsSection")}</Card.Header>
                    <Card.Body>
                        <Form.Check
                            type="switch"
                            id="consentRequired"
                            label={t("consent.settingsRequired")}
                            checked={consentRequired}
                            onChange={(e) => setConsentRequired(e.target.checked)}
                        />
                        <Form.Text className="text-muted d-block">{t("consent.settingsHelp")}</Form.Text>
                        {(() => {
                            const active = preachers.filter((p) => !p.archived);
                            const missingEmail = active.filter((p) => p.email && p.consents?.email?.given !== true).length;
                            const missingWhatsApp = active.filter((p) => p.phoneNumber && p.consents?.whatsapp?.given !== true).length;
                            return (
                                <div className={`small mt-2 ${consentRequired && (missingEmail || missingWhatsApp) ? "text-danger" : "text-muted"}`}>
                                    {t("consent.missingSummary", { email: missingEmail, whatsapp: missingWhatsApp })}
                                </div>
                            );
                        })()}
                    </Card.Body>
                </Card>

                {/* Preacher self-service */}
                <Card className="mb-3">
                    <Card.Header>{t("swap.settingsSection")}</Card.Header>
                    <Card.Body>
                        <Form.Group className="mb-3">
                            <Form.Check
                                type="switch"
                                id="swapRequiresAdminApproval"
                                label={t("swap.settingsApproval")}
                                checked={swapRequiresAdminApproval}
                                onChange={(e) => setSwapRequiresAdminApproval(e.target.checked)}
                            />
                            <Form.Text className="text-muted">{t("swap.settingsApprovalHelp")}</Form.Text>
                        </Form.Group>
                        <Form.Group>
                            <Form.Check
                                type="switch"
                                id="allowDirectPreacherChange"
                                label={t("swap.settingsDirectChange")}
                                checked={allowDirectPreacherChange}
                                onChange={(e) => setAllowDirectPreacherChange(e.target.checked)}
                            />
                            <Form.Text className="text-muted">{t("swap.settingsDirectChangeHelp")}</Form.Text>
                        </Form.Group>
                    </Card.Body>
                </Card>

                {/* Push to the Gemeinde admins */}
                <Card className="mb-3">
                    <Card.Header>
                        <div className="d-flex justify-content-between align-items-center">
                            <span>{t("notificationSettings.pushSection")}</span>
                            <Form.Check
                                type="switch"
                                id="pushEnabled"
                                label={t("notificationSettings.emailEnabled")}
                                checked={config.pushEnabled && channels.pushReleased}
                                disabled={!channels.pushReleased}
                                onChange={(e) => setConfig({ ...config, pushEnabled: e.target.checked })}
                            />
                        </div>
                    </Card.Header>
                    <Card.Body className="text-muted">
                        {channels.pushReleased
                            ? t("notificationSettings.pushHelp")
                            : t("notificationSettings.channelNotReleased")}
                    </Card.Body>
                </Card>

                <Button type="submit" variant="primary" disabled={saving}>
                    {saving ? (
                        <>
                            <Spinner as="span" animation="border" size="sm" className="me-2" />
                            {t("common.loading")}
                        </>
                    ) : (
                        t("admin.save")
                    )}
                </Button>
            </Form>

            {activeGemeindeId && <DeliveryLog gemeindeId={activeGemeindeId} preachers={preachers} />}

            {/* Push notifications for this device */}
            <Card className="mt-4">
                <Card.Header>{t("pushNotifications.section")}</Card.Header>
                <Card.Body>
                    <p className="text-muted mb-3">{t("pushNotifications.description")}</p>
                    {!isFcmSupported() ? (
                        <Alert variant="warning" className="mb-0">
                            {t("pushNotifications.notSupported")}
                        </Alert>
                    ) : pushPermission === "denied" ? (
                        <Alert variant="warning" className="mb-0">
                            {t("pushNotifications.permissionDenied")}
                        </Alert>
                    ) : (
                        <div className="d-flex align-items-center gap-3">
                            <span className={`badge bg-${pushToken ? "success" : "secondary"}`}>
                                {pushToken
                                    ? t("pushNotifications.enabled")
                                    : t("pushNotifications.disabled")}
                            </span>
                            {pushToken ? (
                                <Button
                                    variant="outline-danger"
                                    size="sm"
                                    disabled={pushBusy}
                                    onClick={handleDisablePush}
                                >
                                    {pushBusy ? (
                                        <>
                                            <Spinner as="span" animation="border" size="sm" className="me-2" />
                                            {t("pushNotifications.deactivating")}
                                        </>
                                    ) : (
                                        t("pushNotifications.disable")
                                    )}
                                </Button>
                            ) : (
                                <Button
                                    variant="outline-primary"
                                    size="sm"
                                    disabled={pushBusy}
                                    onClick={handleEnablePush}
                                >
                                    {pushBusy ? (
                                        <>
                                            <Spinner as="span" animation="border" size="sm" className="me-2" />
                                            {t("pushNotifications.activating")}
                                        </>
                                    ) : (
                                        t("pushNotifications.enable")
                                    )}
                                </Button>
                            )}
                        </div>
                    )}
                </Card.Body>
            </Card>
        </div>
    );
}
